<template>
    <v-app>
        <v-container>
            <v-row justify="center" align="center" cols="2">
                <v-col cols="12">
                    <v-alert
                            dismissible
                            type="success"
                            v-if="alert"
                    >{{ alert }}</v-alert>
                </v-col>
                <v-col cols="12">
                    <h1 class="blue-grey--text text--darken-3">Admin Dashboard</h1>
                </v-col>
            </v-row>
            <v-row justify="center" align="center">
              <v-col cols="12">
                <v-expansion-panels>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="text-overline font-weight-black">How to onboard a new client</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="pb-2">
                      <ol>
                        <li>In the first tab (Organizations), fill out the fields under the table. The name should be
                          the full title (e.g. Washington DECA) and the short name are the acronyms (e.g. WA DECA).</li>
                        <li>After adding the organization, click on the 'Websites' tab. In the form under the table, use
                          the dropdown to select the new organization; then enter a domain name (Note: You can add
                          multiple domains for a single organization).</li>
                        <li>Finally, click on the 'Users' tab. In the form below the table, fill out all the fields
                          and click save. You will need to email the user final instructions about how to login and what
                          credentials to use.</li>
                      </ol>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
            <v-row justify="center" align="center" cols="2" class="mb-4">
                <v-col cols="12">
                    <v-tabs
                            class="elevation-3"
                            fixed-tabs
                    >
                        <v-tabs-slider></v-tabs-slider>
                        <v-tab :href="`#tab-1`">
                          Organizations
                        </v-tab>
                        <v-tab-item :value="'tab-1'">
                          <v-card flat tile class="pa-3">
                            <v-card-title>
                              Organizations
                            </v-card-title>
                            <v-card-text>
                              <v-row>
                                <v-col cols="12">
                                  <v-data-table
                                      :headers="orgTableHeader"
                                      :items="orgs"
                                      :items-per-page="10"
                                      class="elevation-1"
                                      :loading="loading"
                                      sort-by="name"
                                  >
                                    <template v-slot:top>
                                      <v-dialog
                                          v-model="dialog"
                                          max-width="600px"
                                          persistent
                                          overlay-opacity="1"
                                      >
                                        <v-card>
                                          <v-card-title class="pb-0 mb-0">
                                            <h3>Editing: {{ editingOrg.shortName}}</h3>
                                          </v-card-title>
                                          <v-card-text>
                                            <v-row align="end" class="pb-0 pt-0">
                                              <v-col cols="10" class="pb-0 pb-0">
                                                <OrganizationDetails :selected-organization="editingOrg"></OrganizationDetails>
                                              </v-col>
                                              <v-col class="text-right mb-3" cols="2">
                                                <v-btn x-small fab color="error" @click="closeDialog" class="mr-2 elevation-0"><v-icon>mdi-close-thick</v-icon></v-btn>
                                                <v-btn x-small fab color="success" @click="updateOrg" class="elevation-0"><v-icon>mdi-check-bold</v-icon></v-btn>
                                              </v-col>
                                            </v-row>
                                          </v-card-text>
                                        </v-card>
                                      </v-dialog>
                                    </template>
                                    <template v-slot:item.action="{ item }">
                                      <v-btn
                                          x-small
                                          color="primary"
                                          v-model="editingOrg"
                                          @click="editOrg(item)">Edit</v-btn>
                                    </template>
                                  </v-data-table>
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col cols="12">
                                  <h4>Add New Organization</h4>
                                  <OrganizationDetails :selected-organization="editingOrg"></OrganizationDetails>
                                  <v-btn @click="newOrg">Submit</v-btn>
                                </v-col>
                              </v-row>
                            </v-card-text>
                          </v-card>
                        </v-tab-item>
                        <v-tab :href="`#tab-2`">
                          Websites
                        </v-tab>
                        <v-tab-item :value="'tab-2'">
                          <v-card flat tile class="pa-3">
                            <v-card-title>
                              Websites
                            </v-card-title>
                            <v-card-text>
                              <v-row>
                                <v-col cols="12">
                                  <v-data-table
                                      :headers="websiteTableHeader"
                                      :items="websites"
                                      :items-per-page="10"
                                      class="elevation-1"
                                      sort-by="organization.name"
                                  ></v-data-table>
                                </v-col>
                              </v-row>
                              <v-row>
                                <v-col cols="12">
                                  <h4>Add New Website</h4>
                                  <v-form ref="newWebsiteForm">
                                    <v-select
                                        :items="orgs"
                                        item-value="id"
                                        item-text="name"
                                        label="Organization"
                                        v-model="websiteOrgID"
                                        class="mb-2"
                                    ></v-select>
                                    <v-text-field
                                        label="Domain"
                                        v-model="websiteDomain"
                                    ></v-text-field>
                                    <v-btn @click="newWebsite">Submit</v-btn>
                                  </v-form>
                                </v-col>
                              </v-row>
                            </v-card-text>
                          </v-card>
                        </v-tab-item>
                        <v-tab :href="`#tab-3`">
                            Users
                        </v-tab>
                        <v-tab-item :value="'tab-3'">
                            <v-card flat tile class="pa-3">
                                <v-card-title>
                                    Users
                                </v-card-title>
                                <v-card-text>
                                    <v-row>
                                        <v-col cols="12">
                                            <v-data-table
                                                    :headers="usersTableHeader"
                                                    :items="users"
                                                    :items-per-page="5"
                                                    class="elevation-1"
                                                    sort-by="organization.name"
                                            ></v-data-table>
                                        </v-col>
                                    </v-row>
                                    <v-row>
                                        <v-col cols="12">
                                            <h4>Add New User</h4>
                                            <v-form ref="newUserForm">
                                                <v-select
                                                        :items="orgs"
                                                        item-value="id"
                                                        item-text="name"
                                                        label="Organization"
                                                        v-model="userOrgID"
                                                        class="mb-2"
                                                ></v-select>
                                                <v-text-field
                                                        label="First Name"
                                                        v-model="userFirstName"
                                                ></v-text-field>
                                                <v-text-field
                                                        label="Last Name"
                                                        v-model="userLastName"
                                                ></v-text-field>
                                                <v-text-field
                                                        label="Email Address"
                                                        v-model="userEmail"
                                                ></v-text-field>
                                                <v-text-field
                                                        label="Username"
                                                        v-model="userName"
                                                ></v-text-field>
                                                <v-text-field
                                                        label="Password"
                                                        v-model="userPassword"
                                                ></v-text-field>
                                                <v-btn @click="newUser">Submit</v-btn>
                                            </v-form>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-tab-item>
                    </v-tabs>
                </v-col>
            </v-row>
        </v-container>
    </v-app>
</template>

<script>
    import { Auth, API, graphqlOperation } from 'aws-amplify';
    import { createOrganization, createUser, createWebsite, updateOrganization } from "../graphql/mutations";
    import { listOrganizations, listUsers, listWebsites, getOrganization } from "../graphql/queries";
    import OrganizationDetails from "@/components/OrganizationDetails";

    export default {
      components: {OrganizationDetails},
      // comments: {'ClientLeads': ClientLeads},
      name: "Admin",
      data: () => ({
          dialog: false,
          loading: true,
          dialogLoading: true,
          alert: '',
          orgTableHeader: [
              // { text: 'ID', align: 'start', value: 'id' },
              { text: 'Name', value: 'name' },
              { text: 'Short Name', value: 'shortName' },
              { text: 'Client Leads', value: 'clientLeads' },
              { text: 'Action', value: 'action', sortable: false },
          ],
          websiteTableHeader: [
              { text: 'Organization', align: 'start', value: 'organization.name' },
              { text: 'Domain', value: 'domain' },
          ],
          usersTableHeader: [
              { text: 'Organization', align: 'start', value: 'organization.name' },
              { text: 'First Name', value: 'firstName' },
              { text: 'Last Name', value: 'lastName' },
              { text: 'Username', value: 'username' },
              { text: 'Email', value: 'email' },
          ],
          newOrgName: '',
          orgID: '',
          websiteDomain: '',
          websiteID: '',
          cognitoID: '',
          userID: '',
          websiteOrgID: '',
          userOrgID: '',
          userFirstName: '',
          userLastName: '',
          userName: '',
          userEmail: '',
          userPassword: '',
          websites:[],
          orgs: [],
          users: [],
          clientLeads: [],
          editingOrg: {}
      }),
      async created() {
          await this.getWebsites();
          await this.getOrganizations();
          await this.getUsers();
          await this.getClientLeads();
          this.loading = false
      },
      methods: {
          scrollToTop() {
              window.scrollTo(0,0);
          },
          async newWebsite() {
              const newWebsite = {
                  domain: this.websiteDomain,
                  orgID: this.websiteOrgID
              };
              await API.graphql(graphqlOperation(createWebsite, { input: newWebsite }))
                  .then((data) => {
                      console.log(data);
                      console.log("Success!");
                      this.getWebsites();
                      this.alert = "Website successfully added!"
                      this.scrollToTop();
                  })
                  .catch((error) => {
                      console.log(error);
                      console.log("Error!");
                  });
            this.$refs.newWebsiteForm.reset();
          },
          async newOrg() {
              await API.graphql(graphqlOperation(createOrganization, {
                  input: {
                      name: this.editingOrg.name,
                      shortName: this.editingOrg.shortName,
                      clientLeads: this.editingOrg.clientLeads
                  }
              }))
                  .then((data) => {
                      console.log(data);
                      console.log("Success!");
                      this.getOrganizations();
                      this.alert = "Organization successfully added!"
                      this.scrollToTop();
                  })
                  .catch((error) => {
                      console.log(error);
                      console.log("Error!");
                  });
            this.$refs.newOrgForm.reset();
          },
          async newUser() {
              await Auth.signUp({
                  username: this.userName,
                  password: this.userPassword,
                  attributes: {
                      email: this.userEmail,
                      given_name: this.userFirstName,
                      family_name: this.userLastName
                  }
              }).then(async (cognito) => {
                  await API.graphql(graphqlOperation(createUser, {
                      input: {
                          orgID: this.userOrgID,
                          cognitoID: cognito.userSub,
                          firstName: this.userFirstName,
                          lastName: this.userLastName,
                          username: this.userName,
                          email: this.userEmail
                      }
                  }))
                      .then((data) => {
                          console.log(data);
                          console.log("User created!");
                          this.getUsers();
                          this.alert = "User successfully added!"
                          this.scrollToTop();
                      })
                      .catch((error) => {
                          console.log(error);
                          console.log("Error!");
                      });
              }).catch((error) => {
                  console.log(error)
              })
              this.$refs.newUserForm.reset();
          },
          async getOrganizations() {
              await API.graphql(graphqlOperation(listOrganizations))
                  .then(result => {
                    console.log(result)
                    this.orgs = result.data.listOrganizations.items
                  })
                  .catch();
          },
          async getWebsites() {
              await API.graphql(graphqlOperation(listWebsites))
                  .then(result => this.websites = result.data.listWebsites.items)
                  .catch();
          },
          async getUsers() {
              await API.graphql(graphqlOperation(listUsers))
                  .then(result => this.users = result.data.listUsers.items)
                  .catch();
          },
          async getClientLeads() {
            await API.graphql(graphqlOperation(getOrganization, { id: "c515f464-6a96-45b3-aabb-92e62a268c3f" }))
                .then(result => console.log(result))
                // .then(result => this.clientLeads = result.data.listUsers.items)
                .catch();
          },
          async updateOrg() {
            const updatedOrg = {
              id: this.editingOrg.id,
              name: this.editingOrg.name,
              shortName: this.editingOrg.shortName,
              clientLeads: this.editingOrg.clientLeads
            }
            await API.graphql(graphqlOperation(updateOrganization, { input: updatedOrg }))
                .then(() => {
                  this.dialog = false;
                  this.editingOrg = {};
                  this.scrollToTop();
                  this.getOrganizations();
                  this.alert = "Organization successfully updated!"
                })
                .catch(e => console.log(e))
          },
          editOrg(org) {
            this.editingOrg = Object.assign({}, org)
            this.dialog = true
            console.log(org.id)
          },
          closeDialog() {
            this.dialog = false;
            this.editingOrg = {}
          }
      }
    }
</script>

<style scoped>

</style>